import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import {
  createWaterMaterial,
  updateWaterParameters,
} from '@ozoneuniverse/mfe-core/dist/components/shaders/water';

interface WaterShaderProps {
  position?: THREE.Vector3;
  scale?: THREE.Vector3;
  rotation?: THREE.Euler;
  instanceId: string;
  asset: any;
  width?: number;
  height?: number;
  resolution?: number;
}

export function WaterShader({
  position = new THREE.Vector3(0, 0, 0),
  scale = new THREE.Vector3(10, 10, 1),
  rotation = new THREE.Euler(-Math.PI / 2, 0, 0),
  instanceId,
  asset,
  width = 10,
  height = 10,
  resolution = 512,
}: WaterShaderProps) {
  const meshRef = useRef<THREE.Mesh>(null);
  const materialRef = useRef<THREE.ShaderMaterial>(null);
  const { scene } = useThree();

  const envMap = React.useMemo(() => {
    if (scene.environment) {
      return scene.environment;
    }

    // Fallback: Create a simple gradient environment map
    // NOTE: default gradient looks better than using environment
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 256;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      const gradient = ctx.createLinearGradient(0, 0, 0, 256);
      gradient.addColorStop(0, '#87CEEB'); // Sky blue
      gradient.addColorStop(1, '#4682B4'); // Steel blue
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 256, 256);
    }

    const texture = new THREE.CanvasTexture(canvas);
    const cubeTexture = new THREE.CubeTexture([
      texture.image,
      texture.image,
      texture.image,
      texture.image,
      texture.image,
      texture.image,
    ]);
    cubeTexture.needsUpdate = true;
    return cubeTexture;
  }, [scene.environment]);

  useEffect(() => {
    if (!materialRef.current) {
      const metadata = asset.metadata || {};
      const shaderParams = metadata.shaderParams || {};
      const materialParams = {
        envMap,
        troughColor: shaderParams.troughColor
          ? new THREE.Color(shaderParams.troughColor)
          : undefined,
        surfaceColor: shaderParams.surfaceColor
          ? new THREE.Color(shaderParams.surfaceColor)
          : undefined,
        peakColor: shaderParams.peakColor
          ? new THREE.Color(shaderParams.peakColor)
          : undefined,
        wavesAmplitude: shaderParams.wavesAmplitude,
        wavesFrequency: shaderParams.wavesFrequency,
        wavesSpeed: shaderParams.wavesSpeed,
        wavesPersistence: shaderParams.wavesPersistence,
        wavesLacunarity: shaderParams.wavesLacunarity,
        wavesIterations: shaderParams.wavesIterations,
        opacity: shaderParams.opacity,
        peakThreshold: shaderParams.peakThreshold,
        peakTransition: shaderParams.peakTransition,
        troughThreshold: shaderParams.troughThreshold,
        troughTransition: shaderParams.troughTransition,
        fresnelScale: shaderParams.fresnelScale,
        fresnelPower: shaderParams.fresnelPower,
      };

      Object.keys(materialParams).forEach((key) => {
        if (materialParams[key] === undefined) {
          delete materialParams[key];
        }
      });

      const material = createWaterMaterial(materialParams);
      materialRef.current = material;

      if (meshRef.current) {
        meshRef.current.material = material;
      }
    }
  }, [envMap]);

  useEffect(() => {
    if (materialRef.current && materialRef.current.uniforms.envMap) {
      materialRef.current.uniforms.envMap.value = envMap;
      materialRef.current.needsUpdate = true;
    }
  }, [envMap]);

  useEffect(() => {
    if (materialRef.current && asset.metadata) {
      const params = asset.metadata.shaderParams;
      const updateParams = {};

      Object.keys(params).forEach((key) => {
        if (key.includes('Color') && typeof params[key] === 'string') {
          updateParams[key] = new THREE.Color(params[key]);
        } else {
          updateParams[key] = params[key];
        }
      });

      updateWaterParameters(materialRef.current, updateParams);
    }
  }, [asset.metadata]);

  useFrame(() => {
    if (materialRef.current && materialRef.current.uniforms.uTime) {
      materialRef.current.uniforms.uTime.value += 0.01;
    }
  });

  useEffect(() => {
    if (meshRef.current && asset.ref) {
      asset.ref.current = meshRef.current;
    }
  }, [asset.ref]);

  return (
    <mesh
      ref={meshRef}
      position={position}
      scale={scale}
      rotation={rotation}
      name={`WaterShader-${instanceId}`}
    >
      <planeGeometry args={[width, height, resolution, resolution]} />
      {materialRef.current && (
        <primitive object={materialRef.current} attach='material' />
      )}
    </mesh>
  );
}
